import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Plus, Loader2, FileText, Save } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { useTenant } from '@/contexts/TenantContext';

export function DocumentReminderSettings() {
  const { currentTenant } = useTenant();
  const [days, setDays] = useState<number[]>([30, 7, 1]);
  const [newDay, setNewDay] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalDays, setOriginalDays] = useState<number[]>([]);

  useEffect(() => {
    if (!currentTenant?.tenant_id) return;

    async function load() {
      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('document_reminder_days')
          .eq('tenant_id', currentTenant!.tenant_id)
          .single();

        if (error) throw error;

        const loaded = data?.document_reminder_days || [30, 7, 1];
        const sorted = [...loaded].sort((a, b) => b - a);
        setDays(sorted);
        setOriginalDays(sorted);
      } catch (err) {
        console.error('Error loading reminder settings:', err);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [currentTenant?.tenant_id]);

  const addDay = () => {
    const value = parseInt(newDay, 10);
    if (isNaN(value) || value < 1 || value > 365) {
      toast.error('Enter a number between 1 and 365');
      return;
    }
    if (days.includes(value)) {
      toast.error(`${value} days is already added`);
      return;
    }
    const updated = [...days, value].sort((a, b) => b - a);
    setDays(updated);
    setNewDay('');
    setHasChanges(true);
  };

  const removeDay = (value: number) => {
    setDays(prev => prev.filter(d => d !== value));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!currentTenant?.tenant_id) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('tenants')
        .update({ document_reminder_days: days })
        .eq('tenant_id', currentTenant.tenant_id);

      if (error) throw error;

      setOriginalDays([...days]);
      setHasChanges(false);
      toast.success('Reminder settings saved');
    } catch (err) {
      console.error('Error saving reminder settings:', err);
      toast.error('Failed to save reminder settings');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDay();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Document Expiry Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Document Expiry Reminders
        </CardTitle>
        <CardDescription>
          Configure when to send reminders before contractor documents expire.
          Notifications go to the document owner and admins.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current intervals */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">
            Send reminders at
          </Label>
          <div className="flex flex-wrap gap-2">
            {days.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No reminders configured. Add intervals below.
              </p>
            ) : (
              days.map((d) => (
                <Badge
                  key={d}
                  variant="secondary"
                  className="text-sm py-1 px-3 gap-1.5"
                >
                  {d} {d === 1 ? 'day' : 'days'} before
                  <button
                    onClick={() => removeDay(d)}
                    className="ml-0.5 hover:text-destructive transition-colors"
                    type="button"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            )}
          </div>
        </div>

        {/* Add new interval */}
        <div className="flex items-end gap-2">
          <div className="space-y-1.5 flex-1 max-w-[200px]">
            <Label htmlFor="new-reminder-day" className="text-sm">
              Add reminder
            </Label>
            <Input
              id="new-reminder-day"
              type="number"
              min={1}
              max={365}
              value={newDay}
              onChange={(e) => setNewDay(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. 14"
              className="text-sm"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={addDay}
            disabled={!newDay}
            className="h-9"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Enter the number of days before expiry to send a reminder (1–365).
        </p>

        {/* Save button */}
        {hasChanges && (
          <div className="flex justify-end pt-2 border-t">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
